import type { Integration, IntegrationCategory } from '@emailforge/schemas';

import { apiSlice } from '@/features/api/apiSlice';

export type MarketplaceResponse = {
  integrations: Integration[];
  categories: IntegrationCategory[];
};

export type IntegrationConnection = {
  id: string;
  slug: string;
  authType: 'oauth' | 'api_key' | 'webhook' | 'built_in';
  status: string;
  connectedAt: string;
  lastUsedAt: string | null;
};

export type ConnectInput =
  | { authType: 'api_key'; credentials: Record<string, string> }
  | { authType: 'webhook' }
  | { authType: 'oauth' }
  | { authType: 'built_in' };

export type ConnectResult = {
  id: string;
  slug: string;
  status: 'connected' | 'pending';
  webhookUrl?: string;
  signingSecret?: string;
};

export type OAuthInitiateResult = {
  url: string;
  note: string;
};

export const integrationsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    marketplace: b.query<MarketplaceResponse, void>({
      query: () => '/integrations/marketplace',
      providesTags: ['Integration'],
    }),
    listIntegrationConnections: b.query<IntegrationConnection[], void>({
      query: () => '/integrations/connections',
      providesTags: ['Integration'],
    }),
    connectIntegration: b.mutation<ConnectResult, { slug: string; input: ConnectInput }>({
      query: ({ slug, input }) => ({
        url: `/integrations/${slug}/connect`,
        method: 'POST',
        body: input,
      }),
      invalidatesTags: ['Integration'],
    }),
    disconnectIntegration: b.mutation<{ ok: true }, string>({
      query: (slug) => ({ url: `/integrations/${slug}/connect`, method: 'DELETE' }),
      invalidatesTags: ['Integration'],
    }),
    oauthInitiate: b.mutation<OAuthInitiateResult, string>({
      query: (slug) => ({ url: `/integrations/${slug}/oauth/initiate`, method: 'GET' }),
    }),
  }),
});

export const {
  useMarketplaceQuery,
  useListIntegrationConnectionsQuery,
  useConnectIntegrationMutation,
  useDisconnectIntegrationMutation,
  useOauthInitiateMutation,
} = integrationsApi;
